import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
} from "@mui/x-data-grid";
import { OrderRowType, PickupOrderRowType } from "../features/orders/OrderData";
import { WeightFreezeRowType } from "../features/weight/WeightData";
import { returnRowType } from "../features/returns/RetrunData";
import { Box, Pagination, Stack, Typography, styled } from "@mui/material";
import { ClientAddressType } from "../../hooks/usePickupAddress";
import { CustomerDetailsType } from "../features/setUp/setupApi";
import { ServiceDetailsType } from "../features/setUp/setupApi";
import { ShipmentProfilesType } from "../features/setUp/ShipmentProfiles";
import { ShipmentData } from "../../pages/WeightDiscrepancies";

interface OrderDataTableProps {
  rows:
    | OrderRowType[]
    | PickupOrderRowType[]
    | WeightFreezeRowType[]
    | ClientAddressType[]
    | returnRowType[]
    | CustomerDetailsType[]
    | ServiceDetailsType[]
    | ShipmentProfilesType[]
    | ShipmentData[]
    | [];
  columns: GridColDef[];
  totalRow?: number;
  page?: number;
  wantExcel?: boolean;
  updatePage?: (e: React.ChangeEvent<unknown>, page: number) => void;
  checkBox?: boolean;
  onRowSelectionModelChange?: (e: GridRowSelectionModel) => void;
  total?: number;
}

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: "10px",
  color: "rgba(0,0,0,.85)",
  WebkitFontSmoothing: "auto",
  letterSpacing: "normal",
  "& .MuiDataGrid-columnsContainer": {
    backgroundColor: "#1d1d1d",
  },
  "& .MuiDataGrid-iconSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-cell": {
    color: "rgba(0,0,0,.85)",
    // minHeight: "50px !important",
    // maxHeight: "fit-content !important",
    // padding: "20px 8px",
  },
  "& .MuiPaginationItem-root": {
    borderRadius: "10px",
  },
  "& .css-1iyq7zh-MuiDataGrid-columnHeaders": {
    backgroundColor: "#e5f0ff",
    borderRadius: "20px",
    boxShadow: "1px 2px 7px 0px rgba(0, 0, 0, 0.06)",
    // backgroundColor: "#ebebeb",
    // padding: "0 14px",
  },

  "& .css-204u17-MuiDataGrid-main": {
    rowGap: "20px",
  },
  "& .css-128fb87-MuiDataGrid-toolbarContainer": {
    marginBottom: "16px",
    columnGap: "24px",
  },
  "& .MuiDataGrid-columnHeaders ": {
    minHeight: "10px !important",
  },
  "& .css-181rldw-MuiButtonBase-root-MuiButton-root": {
    backgroundColor: "#fff",
    borderRadius: "6px",
    border: "0.5px solid #e0e0e0",
    // padding: "2px 10px",
  },
  "& .MuiDataGrid-columnHeader:focus-within": {
    border: "none",
    outline: "none",
    backgroundColor: "#cae1ff",
  },
  "& .MuiDataGrid-cell:focus-within": {
    outline: "none",
    // border: "none",
  },
  "& .MuiDataGrid-row": {
    // padding: "0 14px",
    // minHeight: "50px !important",
    // maxHeight: "fit-content !important",
  },
  ".css-wop1k0-MuiDataGrid-footerContainer": {
    display: "none",
  },

  ".css-i4bv87-MuiSvgIcon-root": {
    width: "16px",
  },

  ".css-qvtrhg-MuiDataGrid-virtualScroller": {
    // overflow: "hidden",
  },
}));

const CustomDataTable = ({
  rows,
  columns,
  totalRow,
  page,
  updatePage,
  wantExcel = true,
  checkBox = false,
  onRowSelectionModelChange,
  total = 0,
}: OrderDataTableProps) => {
  const slots = wantExcel
    ? { toolbar: GridToolbar, noRowsOverlay: CustomNoRowsOverlay }
    : { noRowsOverlay: CustomNoRowsOverlay };

  return (
    <>
      <div
        style={{
          height: `${rows?.length > 0 ? "" : "400px"}`,
        }}
      >
        <StyledDataGrid
          hideFooterPagination
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          slots={slots}
          disableRowSelectionOnClick
          disableDensitySelector
          checkboxSelection={checkBox}
          onRowSelectionModelChange={onRowSelectionModelChange}
          sx={{
            padding: "10px",
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          density="comfortable"
          rows={
            (rows as readonly OrderRowType[]) ||
            (rows as readonly PickupOrderRowType[]) ||
            (rows as readonly WeightFreezeRowType[])
          }
          columns={columns}
        />
      </div>

      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"flex-end"}
        marginTop={"20px"}
      >
        <Typography>Total: {total}</Typography>
        <Pagination count={totalRow} page={page} onChange={updatePage} />
      </Stack>
    </>
  );
};

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .ant-empty-img-1": {
    fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
  },
  "& .ant-empty-img-2": {
    fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
  },
  "& .ant-empty-img-3": {
    fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
  },
  "& .ant-empty-img-4": {
    fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
  },
  "& .ant-empty-img-5": {
    fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
    fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        width="120"
        height="100"
        viewBox="0 0 184 152"
        aria-hidden
        focusable="false"
      >
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse
              className="ant-empty-img-5"
              cx="67.797"
              cy="106.89"
              rx="67.797"
              ry="12.668"
            />
            <path
              className="ant-empty-img-1"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="ant-empty-img-2"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="ant-empty-img-3"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
            />
          </g>
          <path
            className="ant-empty-img-3"
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
          />
          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1 }}>No Rows</Box>
    </StyledGridOverlay>
  );
}

export default CustomDataTable;
